import React from 'react';
import Card, { CardContent } from '../../components/ui/Card';

const CookiePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
            <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

            <div className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files stored on your device when you visit our website. They help us provide a 
                better experience by remembering your preferences, keeping you logged in, and understanding how you use our platform.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Types of Cookies We Use</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Essential Cookies (Required)</h3>
              <p className="text-gray-700 mb-2">
                These cookies are necessary for the platform to function and cannot be disabled:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Cookie Name</th>
                      <th className="text-left py-2 font-semibold">Purpose</th>
                      <th className="text-left py-2 font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">session_token</td>
                      <td className="py-2">Maintains your login session</td>
                      <td className="py-2">15 minutes</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">csrf_token</td>
                      <td className="py-2">Protects against security attacks</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr>
                      <td className="py-2">cookie_consent</td>
                      <td className="py-2">Remembers your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Functional Cookies (Optional)</h3>
              <p className="text-gray-700 mb-2">
                These cookies enhance functionality and personalization:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Cookie Name</th>
                      <th className="text-left py-2 font-semibold">Purpose</th>
                      <th className="text-left py-2 font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">language_preference</td>
                      <td className="py-2">Remembers your language choice</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">theme_preference</td>
                      <td className="py-2">Remembers dark/light mode setting</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2">video_volume</td>
                      <td className="py-2">Remembers video player settings</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Analytics Cookies (Optional)</h3>
              <p className="text-gray-700 mb-2">
                These cookies help us understand how users interact with our platform:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Service</th>
                      <th className="text-left py-2 font-semibold">Purpose</th>
                      <th className="text-left py-2 font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Google Analytics</td>
                      <td className="py-2">Traffic analysis and user behavior</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr>
                      <td className="py-2">Internal Analytics</td>
                      <td className="py-2">Feature usage and performance metrics</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.4 Marketing Cookies (Optional)</h3>
              <p className="text-gray-700 mb-2">
                These cookies track your activity to show relevant advertisements:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-semibold">Service</th>
                      <th className="text-left py-2 font-semibold">Purpose</th>
                      <th className="text-left py-2 font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">Facebook Pixel</td>
                      <td className="py-2">Advertising and retargeting</td>
                      <td className="py-2">90 days</td>
                    </tr>
                    <tr>
                      <td className="py-2">Google Ads</td>
                      <td className="py-2">Advertising and conversion tracking</td>
                      <td className="py-2">90 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We use the following third-party services that may set cookies:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Stripe:</strong> Payment processing and fraud prevention</li>
                <li><strong>Google Analytics:</strong> Usage statistics and analytics</li>
                <li><strong>YouTube:</strong> Embedded video content</li>
                <li><strong>Social Media Platforms:</strong> Sharing and embedded content</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Managing Your Cookie Preferences</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Cookie Consent Banner</h3>
              <p className="text-gray-700 mb-4">
                When you first visit our platform, you'll see a cookie consent banner allowing you to accept or reject 
                optional cookies. You can change these preferences at any time.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Browser Settings</h3>
              <p className="text-gray-700 mb-4">
                Most browsers allow you to control cookies through their settings:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Cookies and Site Permissions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Opt-Out Tools</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-600 hover:underline">Google Analytics Opt-out</a></li>
                <li><a href="http://www.aboutads.info/choices/" className="text-blue-600 hover:underline">Digital Advertising Alliance</a></li>
                <li><a href="http://www.youronlinechoices.com/" className="text-blue-600 hover:underline">Your Online Choices (EU)</a></li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Impact of Disabling Cookies</h2>
              <p className="text-gray-700 mb-4">
                If you disable certain cookies, some features may not function properly:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>You may need to log in repeatedly</li>
                <li>Your preferences won't be saved</li>
                <li>Some features may not work correctly</li>
                <li>Payment processing may be affected</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Cookie Policy to reflect changes in our practices or applicable laws. We will notify 
                you of significant changes through our platform.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about our use of cookies, contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700"><strong>Email:</strong> privacy@patreonclone.com</p>
                <p className="text-gray-700"><strong>Address:</strong> [Your Company Address]</p>
              </div>

              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Quick Tip:</strong> You can manage your cookie preferences at any time from the footer of our website.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
